import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { formatISO } from 'date-fns';
import Button from '../../../shared/components/ui/Button';
import FormInput from '../../../shared/components/ui/FormInput';
import toast from 'react-hot-toast';
import { updateDealTransaction } from '../../../builder/services/api/implementation/impl';

const UpdatePaymentStatusModal = ({
    isOpen,
    onClose,
    dealId,
    onSuccess
}) => {
    // Single payment state
    const [payment, setPayment] = useState({
        amount_paid: '',
        payment_date: '',
        payment_mode: '',
        transaction_id: '',
        remarks: ''
    });

    // Payment mode options
    const paymentModeOptions = [
        { value: 'cheque', label: 'Cheque' },
        { value: 'upi', label: 'UPI' },
        { value: 'internet_banking', label: 'Internet Banking' },
        { value: 'rtgs', label: 'RTGS' },
        { value: 'nft', label: 'NFT' },
        { value: 'cash', label: 'Cash' }
    ];

    const {
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm();

    // Reset form when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setPayment({
                amount_paid: '',
                payment_date: '',
                payment_mode: '',
                transaction_id: '',
                remarks: ''
            });
        }
    }, [isOpen]);

    const sanitizeToNumber = (val) => {
        if (val === null || val === undefined) return NaN;
        const s = String(val)
            .replace(/[^0-9.-]/g, '')
            .trim();
        const n = Number(s);
        return Number.isFinite(n) ? n : NaN;
    };

    // Helper function to get transaction ID label based on payment mode
    const getTransactionIdLabel = (paymentMode) => {
        switch (paymentMode) {
            case 'cheque':
                return 'Check Number';
            case 'cash':
                return null; // No field for cash
            default:
                return 'Transaction ID';
        }
    };

    // Payment handling functions
    const handlePaymentChange = (field, value) => {
        const newPayment = { ...payment };
        newPayment[field] = value;

        // Reset specific fields when mode changes
        if (field === 'payment_mode') {
            // Keep amount and date, reset transaction ID
            newPayment.transaction_id = '';
        }

        setPayment(newPayment);
    };

    // Dummy register function for FormInput components
    const dummyRegister = (fieldName) => ({
        onChange: () => { },
        onBlur: () => { },
        name: fieldName,
        ref: () => { }
    });

    const resetPaidModal = () => {
        setPayment({
            amount_paid: '',
            payment_date: '',
            payment_mode: '',
            transaction_id: '',
            remarks: ''
        });
        reset();
        onClose();
    };

    const { mutate: handleUpdateStatus } = useMutation({
        mutationFn: () => {
            // Validate payment
            if (!payment.payment_mode) {
                toast.error('Please select payment mode.');
                return Promise.reject(new Error('VALIDATION_ERROR'));
            }

            if (payment.amount_paid === '' || !payment.payment_date) {
                toast.error('Please fill amount and date.');
                return Promise.reject(new Error('VALIDATION_ERROR'));
            }

            const amt = sanitizeToNumber(payment.amount_paid);
            if (!Number.isFinite(amt) || amt < 0) {
                toast.error('Please enter a valid amount.');
                return Promise.reject(new Error('VALIDATION_ERROR'));
            }

            if (payment.payment_mode !== 'cash' && !payment.transaction_id) {
                const fieldName = payment.payment_mode === 'cheque' ? 'Check Number' : 'Transaction ID';
                toast.error(`Please fill ${fieldName} for ${payment.payment_mode} payment.`);
                return Promise.reject(new Error('VALIDATION_ERROR'));
            }

            const payload = {
                amount_paid: amt,
                mode: payment.payment_mode,
                transactionID: payment.payment_mode === 'cash' ? null : payment.transaction_id,
                remarks: payment.remarks || '',
                date: formatISO(new Date(payment.payment_date))
            };

            return updateDealTransaction(dealId, payload);
        },
        onSuccess: () => {
            toast.success('Payment Status Updated Successfully');
            resetPaidModal();
            if (onSuccess) {
                onSuccess();
            }
        },
        onError: (err) => {
            if (err.message === 'VALIDATION_ERROR') return;
            toast.error('Error updating payment status');
        }
    });

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-[55%] max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-semibold text-gray-800">Update Payment Status</h2>
                        <button
                            onClick={resetPaidModal}
                            className="text-gray-400 hover:text-gray-600 transition-colors">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>

                    <div className="flex justify-center flex-col mb-8">
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleUpdateStatus();
                            }}
                            className="flex flex-col gap-4 pt-6">

                            <div className="flex flex-col gap-4 w-full border border-gray-200 rounded-lg p-4">

                                {/* Payment Mode Selection */}
                                <div className="w-full">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Payment Mode <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={payment.payment_mode}
                                        onChange={(e) => {
                                            handlePaymentChange('payment_mode', e.target.value);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        required>
                                        <option value="">Select Payment Mode</option>
                                        {paymentModeOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Conditional Fields Based on Payment Mode */}
                                {payment.payment_mode && (
                                    <div className="flex flex-col gap-4">

                                        <div className="flex gap-4 items-end w-full">
                                            <FormInput
                                                width={49}
                                                label={'How much are you paying'}
                                                isRequired
                                                register_key={`payingAmount`}
                                                type={'number'}
                                                valuePre={payment.amount_paid}
                                                onType={(val) =>
                                                    handlePaymentChange(
                                                        'amount_paid',
                                                        val.target ? val.target.value : val
                                                    )
                                                }
                                                register={dummyRegister}
                                            />
                                            <FormInput
                                                width={49}
                                                label={'Payment Date'}
                                                isRequired
                                                register_key={`paymentDate`}
                                                type={'date'}
                                                valuePre={payment.payment_date}
                                                CustomFeature={{
                                                    max: new Date().toISOString().split('T')[0]
                                                }}
                                                onType={(val) =>
                                                    handlePaymentChange(
                                                        'payment_date',
                                                        val.target ? val.target.value : val
                                                    )
                                                }
                                                register={dummyRegister}
                                            />
                                        </div>

                                        {/* Transaction ID */}
                                        {payment.payment_mode !== 'cash' && (
                                            <div className="w-full">
                                                <FormInput
                                                    width={100}
                                                    label={getTransactionIdLabel(payment.payment_mode)}
                                                    isRequired
                                                    register_key={`transactionId`}
                                                    type={'text'}
                                                    valuePre={payment.transaction_id}
                                                    onType={(val) =>
                                                        handlePaymentChange(
                                                            'transaction_id',
                                                            val.target ? val.target.value : val
                                                        )
                                                    }
                                                    register={dummyRegister}
                                                />
                                            </div>
                                        )}

                                        {/* Remarks Field */}
                                        <div className="w-full">
                                            <FormInput
                                                width={100}
                                                label={'Remarks'}
                                                register_key={`remarks`}
                                                type={'text'}
                                                placeholder={'Add any additional notes (optional)'}
                                                valuePre={payment.remarks}
                                                onType={(val) =>
                                                    handlePaymentChange('remarks', val.target ? val.target.value : val)
                                                }
                                                register={dummyRegister}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="w-full flex justify-end items-center space-x-4 mt-8">
                                <Button type="button" onClick={resetPaidModal} displayType="secondary">
                                    Cancel
                                </Button>
                                <Button type="submit">Update</Button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UpdatePaymentStatusModal;
